import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import {
    mainActions,
    useCreateProjectTagItemMutation,
    useCreateProjectTagTakeoffMutation,
    useGetConfigQuery,
    useGetProjectReadyQuery,
    useUpdateProjectTagQtyMutation,
    useUpdateProjectTagNameMutation,
    useUpdateProjectTagEquipTypeMutation,
    useUpdateProjectTagRoomMutation,
    useUpdateProjectTagTradeMutation,
    useClearProjectTagTradeMutation,
    useClearProjectTagEquipTypeMutation,
    useClearProjectTagRoomMutation,
    useGetAllProductsQuery,
    useCreateConfiguredItemMutation,
    mainSelectors,
} from '../../../store';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { ChevronButton, CheckboxField } from '../../../components';
import classNames from 'classnames';
import Creatable from 'react-select/creatable';
import { TagManager } from './TagManager';
import { TagMenuItem } from './TagMenuItem';
import { ProductRows } from './ProductRows';
import { ToolTip } from '../../../components';
import { EditTagModal } from '../../modals/EditTagModal';
import { DuplicateTagModal } from '../../modals/DuplicateTagModal';
import { DeleteTagModal } from '../../modals/DeleteTagModal';
import { AddProductModal } from '../../modals/AddProductModal';
import { AddConfigProductModal } from '../../modals/AddConfigProductModal';
import lodashOrderBy from 'lodash/orderBy';
import * as lodash from 'lodash';

export const TagRow = (props) => {
    const {
        allOpen,
        tag,
        projectId,
        index,
        OnTagQtyChangeFail,
        OnTagNameChangeFail,
        isDraggable = true,
        checkedItemIds,
        checkedTagIds,
        onItemCheckboxToggle,
        onTagCheckboxToggle,
    } = props;
    const dispatch = useDispatch();
    const selectedTagId = useSelector(mainSelectors.getSelectedTagId);
    const [newEditTagModalOpen, setNewEditTagModalOpen] = useState(false);
    const [newDuplicateTagModalOpen, setNewDuplicateTagModalOpen] =
        useState(false);
    const [newDeleteTagModalOpen, setNewDeleteTagModalOpen] = useState(false);
    const [newAddProductModalOpen, setNewAddProductModalOpen] = useState(false);
    const [newAddConfigProdModalOpen, setNewAddConfigProdModalOpen] =
        useState(false);
    const [open, setOpen] = useState(allOpen);

    useEffect(() => {
        setOpen(allOpen);
    }, [allOpen]);

    const { data: config } = useGetConfigQuery();
    const [createTagItem] = useCreateProjectTagItemMutation();
    const [createTagTakeoff] = useCreateProjectTagTakeoffMutation();
    const [updateTagQty] = useUpdateProjectTagQtyMutation();
    const [updateTagName] = useUpdateProjectTagNameMutation();
    const [updateTagEquipType] = useUpdateProjectTagEquipTypeMutation();
    const [updateTagRoom] = useUpdateProjectTagRoomMutation();
    const [updateTagTrade] = useUpdateProjectTagTradeMutation();
    const [clearTagTrade] = useClearProjectTagTradeMutation();
    const [clearTagEquipType] = useClearProjectTagEquipTypeMutation();
    const [clearTagRoom] = useClearProjectTagRoomMutation();
    const { data: projectIsReady } = useGetProjectReadyQuery(projectId);
    const { data: products, isLoading: isProductLoading } =
        useGetAllProductsQuery();
    const [createConfiguredItem] = useCreateConfiguredItemMutation();

    // section to handle all in-line change to tag
    const [tagName, setTagName] = useState(tag.value);
    const [mouseEnteredTagName, setMouseEnteredTagName] = useState(false);
    const [tagRoom, setTagRoom] = useState(tag.room);
    const [mouseEnteredTagRoom, setMouseEnteredTagRoom] = useState(false);
    const [tagQty, setTagQty] = useState(tag.quantity);
    const [mouseEnteredTagQty, setMouseEnteredTagQty] = useState(false);

    const currTrade = useMemo(() => {
        const returnValue = tag.trade ? tag.trade : '';
        return { value: returnValue, label: returnValue };
    }, [tag.trade]);

    const currEquipType = useMemo(() => {
        const returnValue = tag.equipmentType ? tag.equipmentType : '';
        return { value: returnValue, label: returnValue };
    }, [tag.equipmentType]);

    const equipmentList = config?.options.equipmentList;
    const tradeList = config?.options.tradeList;

    const defaultNewItem = useMemo(() => {
        let result = null;

        if (!isProductLoading && products) {
            result =
                lodash.head(
                    lodashOrderBy(
                        products.map((prod) => {
                            return {
                                label: prod.name,
                                value: prod.id,
                            };
                        }),
                        ['label'],
                        ['asc'],
                    ),
                ) || null;
        }
        return result;
    }, [products, isProductLoading]);

    const disableNewItem = useMemo(() => {
        return isProductLoading || !defaultNewItem;
    }, [isProductLoading, defaultNewItem]);

    const onAddSkuItem = (projectId, tag, productId, prodQty) => {
        createConfiguredItem({ id: projectId, tag, productId, qty: prodQty });
    };

    const saveNewItem = async (value) => {
        const itemQty =
            value?.fields?.find((x) => x.name === 'fQuantity')?.value ?? 1;
        const itemFix = value?.fields?.find((x) => x.name === 'rebarSelection');
        if (itemFix) {
            itemFix.stringValue = itemFix.stringValue + '';
        }
        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: 'Saving new product item ...',
            }),
        );
        try {
            await createTagItem({
                id: tag.projectId,
                tagId: tag.id,
                qty: itemQty,
                config: JSON.stringify(value),
            }).unwrap();
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
        }
    };

    const saveNewTakeoff = async (value) => {
        const itemQty =
            value?.fields?.find((x) => x.name === 'fQuantity')?.value ?? 1;
        const itemFix = value?.fields?.find((x) => x.name === 'rebarSelection');
        if (itemFix) {
            itemFix.stringValue = itemFix.stringValue + '';
        }
        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: 'Saving new product item ...',
            }),
        );
        try {
            await createTagTakeoff({
                id: tag.projectId,
                tagId: tag.id,
                qty: itemQty,
                config: JSON.stringify(value),
            }).unwrap();
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
        }
    };

    const saveTagQty = async (value) => {
        let opSuccess = false;
        if (value !== tag.quantity) {
            dispatch(
                mainActions.showWaitingModal({
                    isSimple: true,
                    msg: 'Saving tag quantity ...',
                }),
            );
            try {
                opSuccess = await updateTagQty({
                    id: tag.projectId,
                    tagId: tag.id,
                    qty: value,
                }).unwrap();
            } catch (err) {
                console.log(err);
            } finally {
                dispatch(mainActions.hideWaitingModal());
                if (!opSuccess) {
                    OnTagQtyChangeFail(true);
                }
            }
        }
    };

    const saveTagName = async (value) => {
        let opSuccess = false;
        if (value !== '' && value !== tag.value) {
            dispatch(
                mainActions.showWaitingModal({
                    isSimple: true,
                    msg: 'Saving tag name ...',
                }),
            );
            try {
                opSuccess = await updateTagName({
                    id: tag.projectId,
                    tagId: tag.id,
                    name: value,
                }).unwrap();
            } catch (err) {
                console.log(err);
            } finally {
                dispatch(mainActions.hideWaitingModal());
                if (!opSuccess) {
                    OnTagNameChangeFail(true);
                }
            }
        }
    };

    const saveTagEquipType = async (value) => {
        if (value !== tag.equipmentType) {
            dispatch(
                mainActions.showWaitingModal({
                    isSimple: true,
                    msg: 'Saving tag equipment type ...',
                }),
            );
            try {
                if (value !== '') {
                    await updateTagEquipType({
                        id: tag.projectId,
                        tagId: tag.id,
                        equipType: value,
                    }).unwrap();
                } else {
                    await clearTagEquipType({
                        id: tag.projectId,
                        tagId: tag.id,
                    }).unwrap();
                }
            } catch (err) {
                console.log(err);
            } finally {
                dispatch(mainActions.hideWaitingModal());
            }
        }
    };

    const saveTagRoom = async (value) => {
        if (value !== tag.room) {
            dispatch(
                mainActions.showWaitingModal({
                    isSimple: true,
                    msg: 'Saving tag room ...',
                }),
            );
            try {
                if (value !== '') {
                    await updateTagRoom({
                        id: tag.projectId,
                        tagId: tag.id,
                        room: value,
                    }).unwrap();
                } else {
                    await clearTagRoom({
                        id: tag.projectId,
                        tagId: tag.id,
                    }).unwrap();
                }
            } catch (err) {
                console.log(err);
            } finally {
                dispatch(mainActions.hideWaitingModal());
            }
        }
    };

    const saveTagTrade = async (value) => {
        if (value !== tag.trade) {
            dispatch(
                mainActions.showWaitingModal({
                    isSimple: true,
                    msg: 'Saving tag trade ...',
                }),
            );
            try {
                if (value !== '') {
                    await updateTagTrade({
                        id: tag.projectId,
                        tagId: tag.id,
                        trade: value,
                    }).unwrap();
                } else {
                    await clearTagTrade({
                        id: tag.projectId,
                        tagId: tag.id,
                    }).unwrap();
                }
            } catch (err) {
                console.log(err);
            } finally {
                dispatch(mainActions.hideWaitingModal());
            }
        }
    };

    const hasItemToValidate = useMemo(() => {
        return tag.items.some((item) => item.needValidate);
    }, [tag.items]);

    const hasItemNeedAction = useMemo(() => {
        return tag.items.some((item) => item.needAction);
    }, [tag.items]);

    const hasErrorItems = useMemo(() => {
        function hasConfigJson(item) {
            return item.configuratorJson !== null;
        }
        const itemsHasConfigJson = tag.items.filter(hasConfigJson);
        const jsonObjects = itemsHasConfigJson.map((item) => {
            return JSON.parse(item.configuratorJson);
        });
        return jsonObjects.some((item) => item.configuredProduct?.hasErrors);
    }, [tag.items]);

    const allItemsChecked = useMemo(() => {
        return checkedTagIds?.has(tag.id);
    }, [tag.id, checkedTagIds]);

    const isSelected = useMemo(() => {
        return selectedTagId === tag.id;
    }, [tag, selectedTagId]);

    const rowClassName = useMemo(() => {
        if (isSelected) {
            return 'border-b border-t border-black-20 font-bold text-white bg-green-dark';
        }
        if (allItemsChecked) {
            return 'border-b border-t border-black-20 font-bold bg-green-secondary';
        }
        return 'border-b border-t border-black-20 font-bold hover:bg-gray-5';
    }, [isSelected, allItemsChecked]);

    const tagNameLeaveHandler = () => {
        if (isSelected && mouseEnteredTagName) {
            if (tagName.trim() !== '' && tagName !== tag.value) {
                saveTagName(tagName);
            }
            setMouseEnteredTagName(false);
        }
    };

    const tagQtyLeaveHandler = () => {
        if (mouseEnteredTagQty) {
            if (tagQty >= 1) {
                saveTagQty(tagQty);
            }
            setMouseEnteredTagQty(false);
        }
    };

    const tagRoomLeaveHandler = () => {
        if (mouseEnteredTagRoom) {
            if (tagRoom.trim() !== tag.room) {
                saveTagRoom(tagRoom.trim());
            }
            setMouseEnteredTagRoom(false);
        }
    };

    const numFieldValueChangeHandler = (event) => {
        if (event.target.value === '') {
            setTagQty(1);
        } else if (parseInt(event.target.value) < 1) {
            setTagQty(1);
        } else {
            setTagQty(+event.target.value);
        }
    };

    const keyUpHandler = (event) => {
        if (event.key === 'Enter') {
            if (tagQty >= 1) {
                saveTagQty(tagQty);
            }
            setMouseEnteredTagQty(false);
        }
    };

    const takeoff = {
        icon: <FontAwesomeIcon icon="fa-solid fa-plus" />,
        text: 'Takeoff',
        configId: config?.configurators.Takeoff,
    };

    const showTagWithWarning = (tagName) => {
        return (
            <div className="flex flex-row">
                <div>{tagName}</div>
                <div className="ml-2 text-yellow-100">{'*'}</div>
            </div>
        );
    };

    const showTagWithError = (tagName) => {
        return (
            <div className="flex flex-row text-red-100">
                <div>{tagName}</div>
                <FontAwesomeIcon icon="fa-solid fa-circle-info" />
            </div>
        );
    };

    const tagClassName = useMemo(() => {
        return hasErrorItems ? 'text-red-100' : '';
    }, [hasErrorItems]);

    const onRowClickHandler = (event, isSelected, selectedTagId) => {
        event.stopPropagation();
        if (!isSelected) {
            dispatch(mainActions.setSelectedTagId(selectedTagId));
            dispatch(mainActions.setSelectedItemId(null));
        } else {
            dispatch(mainActions.setSelectedTagId(null));
        }
    };

    const onChevronBtnClickHandler = (event, openStatus) => {
        event.stopPropagation();
        setOpen(openStatus);
    };

    const tagNameChangeHandler = (event) => {
        setTagName(event.target.value);
    };

    const equipTypeChangeHandler = (value) => {
        if (value.trim() !== tag.equipmentType) {
            saveTagEquipType(value.trim());
        }
    };
    const tagRoomChangeHandler = (event) => {
        setTagRoom(event.target.value);
    };

    const tagTradeChangeHandler = (value) => {
        if (value.trim() !== tag.trade) {
            saveTagTrade(value.trim());
        }
    };
    const geneircClickHandler = (event) => {
        event.stopPropagation();
    };

    const renderTagName = (isSelected) => {
        if (!isSelected) {
            if (hasItemToValidate || hasItemNeedAction) {
                return showTagWithWarning(tag.value);
            } else if (hasErrorItems) {
                return showTagWithError(tag.value);
            } else {
                return tag.value;
            }
        } else {
            return (
                <div
                    className="swegon-ui-text-input-wrapper"
                    onMouseEnter={() => {
                        setMouseEnteredTagName(!mouseEnteredTagName);
                    }}
                    onMouseLeave={tagNameLeaveHandler}
                    onClick={geneircClickHandler}
                >
                    <input
                        id="tagName"
                        type="text"
                        value={tagName}
                        onChange={tagNameChangeHandler}
                    />
                </div>
            );
        }
    };

    const renderTagEquipType = (isSelected) => {
        if (!isSelected) {
            return tag.equipmentType;
        } else {
            return (
                <div
                    className="swegon-ui-text-input-wrapper min-w-[150px]"
                    onClick={geneircClickHandler}
                >
                    <Creatable
                        styles={{
                            control: (styles) => ({
                                ...styles,
                                backgroundColor: 'white',
                                minWidth: '150px',
                                height: '32px',
                                minHeight: '32px',
                                borderRadius: '0.125rem',
                            }),
                            option: (styles, { isFocused, isSelected }) => ({
                                ...styles,
                                backgroundColor: isSelected
                                    ? '#277D32'
                                    : isFocused
                                      ? '#39AA0D'
                                      : 'white',
                                color: 'black',
                            }),
                            menu: (styles) => ({
                                ...styles,
                                backgroundColor: 'white',
                            }),
                            singleValue: (styles) => ({
                                ...styles,
                                color: 'black',
                            }),
                        }}
                        isClearable
                        menuPlacement="bottom"
                        value={currEquipType}
                        options={
                            equipmentList
                                ? equipmentList.map((value) => ({
                                      value,
                                      label: value,
                                  }))
                                : []
                        }
                        name={tag.id + '-equipType'}
                        onChange={(selectedOption) => {
                            console.log('selection changes...');
                            console.log(selectedOption);
                            const saveValue = selectedOption
                                ? selectedOption.value
                                : '';
                            console.log(`selection value ${saveValue} ...`);
                            equipTypeChangeHandler(saveValue);
                        }}
                    />
                </div>
            );
        }
    };

    const renderTagRoom = (isSelected) => {
        if (!isSelected) {
            return tag.room;
        } else {
            return (
                <div
                    className="swegon-ui-text-input-wrapper "
                    onMouseEnter={() => {
                        setMouseEnteredTagRoom(!mouseEnteredTagRoom);
                    }}
                    onMouseLeave={tagRoomLeaveHandler}
                    onClick={geneircClickHandler}
                >
                    <input
                        id="tagRoom"
                        type="text"
                        value={tagRoom}
                        onChange={tagRoomChangeHandler}
                    />
                </div>
            );
        }
    };

    const renderTagTrade = (isSelected) => {
        if (!isSelected) {
            return tag.trade;
        } else {
            return (
                <div
                    className="swegon-ui-text-input-wrapper min-w-[150px]"
                    onClick={geneircClickHandler}
                >
                    <Creatable
                        styles={{
                            control: (styles) => ({
                                ...styles,
                                backgroundColor: 'white',
                                height: '32px',
                                minHeight: '32px',
                                borderRadius: '0.125rem',
                            }),
                            option: (styles, { isFocused, isSelected }) => ({
                                ...styles,
                                backgroundColor: isSelected
                                    ? '#277D32'
                                    : isFocused
                                      ? '#39AA0D'
                                      : 'white',
                                color: 'black',
                            }),
                            menu: (styles) => ({
                                ...styles,
                                backgroundColor: 'white',
                            }),
                            singleValue: (styles) => ({
                                ...styles,
                                color: 'black',
                            }),
                        }}
                        isClearable
                        menuPlacement="bottom"
                        value={currTrade}
                        options={
                            tradeList
                                ? tradeList.map((value) => ({
                                      value,
                                      label: value,
                                  }))
                                : []
                        }
                        name={tag.id + '-trade'}
                        onChange={(selectedOption) => {
                            console.log('in tag trade selection changes...');
                            console.log(selectedOption);
                            const saveValue = selectedOption
                                ? selectedOption.value
                                : '';
                            tagTradeChangeHandler(saveValue);
                        }}
                    />
                </div>
            );
        }
    };

    const renderTagQty = (isSelected) => {
        return (
            <div
                className="swegon-ui-quantity-input-wrapper"
                onMouseEnter={() => {
                    setMouseEnteredTagQty(!mouseEnteredTagQty);
                }}
                onMouseLeave={tagQtyLeaveHandler}
            >
                <input
                    type="number"
                    defaultValue={tagQty}
                    min={1}
                    onClick={geneircClickHandler}
                    onChange={numFieldValueChangeHandler}
                    onKeyUp={keyUpHandler}
                    disabled={!mouseEnteredTagQty && projectIsReady}
                />
            </div>
        );
    };

    const renderContent = (provided) => (
        <tbody
            ref={provided?.innerRef}
            {...provided?.draggableProps}
            {...provided?.dragHandleProps}
        >
            <tr
                className={rowClassName}
                onClick={(event) => {
                    onRowClickHandler(event, isSelected, tag.id);
                }}
            >
                <td onClick={(e) => e.stopPropagation()}>
                    <CheckboxField
                        checked={allItemsChecked}
                        onChange={() =>
                            onTagCheckboxToggle?.(tag.id, !allItemsChecked)
                        }
                        title={`Select all items in tag ${tag.value}`}
                    />
                </td>
                <td className="pl-1 pr-1">
                    <div className="flex gap-1 items-center">
                        <ChevronButton
                            open={open}
                            onClick={(event) => {
                                onChevronBtnClickHandler(event, !open);
                            }}
                            title="Toggle product list"
                        />
                    </div>
                </td>
                <td className="pl-1 pr-4">{renderTagName(isSelected)}</td>
                <td className={tagClassName}>
                    {renderTagEquipType(isSelected)}
                </td>
                <td className={tagClassName}>{renderTagRoom(isSelected)}</td>
                <td className={tagClassName}>{renderTagTrade(isSelected)}</td>
                <td />
                <td className={tagClassName}>{renderTagQty(isSelected)}</td>
                <td className="justify-center items-center flex">
                    <button
                        className="swegon-like-button white"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAddSkuItem(
                                tag.projectId,
                                tag,
                                defaultNewItem.value,
                                1,
                            );
                        }}
                        disabled={disableNewItem}
                        title="Add SKU Product"
                    >
                        <FontAwesomeIcon icon="fa-solid fa-plus" />
                    </button>
                </td>
                <td>
                    <ToolTip
                        anchor={
                            <button
                                className={classNames(
                                    'swegon-like-button white',
                                    projectIsReady ? '' : 'text-gray-30',
                                )}
                                onClick={(e) => e.stopPropagation()}
                                title="Tag options menu"
                            >
                                <FontAwesomeIcon icon="fa-solid fa-ellipsis" />
                            </button>
                        }
                        place="bottom-start"
                        noArrow="true"
                        opsDisable={!projectIsReady}
                        opPhase="perform some or all menu options"
                    >
                        <div className="divide-y-2 divide-solid">
                            <div className="mb-2">
                                <TagManager
                                    setNewEditTagModalOpen={
                                        setNewEditTagModalOpen
                                    }
                                    setNewDuplicateTagModalOpen={
                                        setNewDuplicateTagModalOpen
                                    }
                                    setNewDeleteTagModalOpen={
                                        setNewDeleteTagModalOpen
                                    }
                                    setNewAddProductModalOpen={
                                        setNewAddProductModalOpen
                                    }
                                    setNewAddConfigProdModalOpen={
                                        setNewAddConfigProdModalOpen
                                    }
                                    opsDisable={!projectIsReady}
                                />
                            </div>
                            <div className="mt-2">
                                <TagMenuItem
                                    key={takeoff.text}
                                    icon={takeoff.icon}
                                    text={takeoff.text}
                                    configId={takeoff.configId}
                                    saveHandler={saveNewTakeoff}
                                    tagName={tag.value}
                                    opsDisable={!projectIsReady}
                                />
                            </div>
                        </div>
                    </ToolTip>
                </td>
            </tr>
        </tbody>
    );

    return (
        <>
            {isDraggable ? (
                <Draggable
                    draggableId={tag.id}
                    index={index}
                >
                    {(provided) => renderContent(provided)}
                </Draggable>
            ) : (
                renderContent()
            )}
            {open ? (
                <Droppable
                    droppableId={tag.id}
                    type="PRODUCT"
                >
                    {(provided) => (
                        <tbody
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            <ProductRows
                                tag={tag}
                                projectId={projectId}
                                opsDisable={!projectIsReady}
                                checkedItemIds={checkedItemIds}
                                onItemCheckboxToggle={onItemCheckboxToggle}
                            />
                            {provided.placeholder}
                        </tbody>
                    )}
                </Droppable>
            ) : null}
            <EditTagModal
                show={newEditTagModalOpen}
                onClose={() => setNewEditTagModalOpen(false)}
                projectId={projectId}
                tag={tag}
            />
            <DuplicateTagModal
                show={newDuplicateTagModalOpen}
                onClose={() => setNewDuplicateTagModalOpen(false)}
                projectId={projectId}
                tag={tag}
            />
            <DeleteTagModal
                show={newDeleteTagModalOpen}
                onClose={() => setNewDeleteTagModalOpen(false)}
                tag={tag}
                projectId={projectId}
            />
            <AddProductModal
                show={newAddProductModalOpen}
                onClose={() => setNewAddProductModalOpen(false)}
                tag={tag}
                projectId={projectId}
            />
            <AddConfigProductModal
                show={newAddConfigProdModalOpen}
                onClose={() => setNewAddConfigProdModalOpen(false)}
                tag={tag}
                projectId={projectId}
                saveHandler={saveNewItem}
            />
        </>
    );
};
